import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Activity, ArrowLeft, Info, Plug, Server, Wrench } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { IntegrationService } from "../services/integrationsService";
import { instanceKind, mcpOAuthOf } from "../utils/integrationKind";
import {
  integrationBaseQueryKey,
  readOAuthReturn,
  resolveIntegrationTab,
  type IntegrationTab,
} from "../utils/integrationTabs";
import { IntegrationGeneralTab } from "./IntegrationGeneralTab";
import { IntegrationConnectionTab } from "./IntegrationConnectionTab";
import { IntegrationToolsTab } from "./IntegrationToolsTab";
import { IntegrationUsageTab } from "./IntegrationUsageTab";

/**
 * A página de uma integração instalada (AB#87542), no lugar do modal de edição.
 *
 * Mesmo desenho da tela de uma skill: identidade no cabeçalho, FORA das abas — vale para
 * todas elas —, e as abas abaixo. A aba vive em `?tab=`, como no Brain, e pode ser
 * compartilhada.
 */
const IntegrationDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo(() => resolveIntegrationTab(searchParams.get("tab")), [searchParams]);

  const {
    data: integration,
    isLoading,
    isError,
  } = useQuery({
    queryKey: integrationBaseQueryKey(id),
    queryFn: () => IntegrationBaseService.getById(id),
    enabled: !!id,
  });

  // O template diz quais campos de configuração e credencial existem (api) e é a
  // referência do "Comparar com o template".
  const { data: template } = useQuery({
    queryKey: ["integration-template", integration?.integration_id],
    queryFn: () => IntegrationService.getById(integration!.integration_id),
    enabled: !!integration?.integration_id,
    staleTime: 1000 * 60 * 5,
  });

  // Retorno do OAuth de um servidor MCP (AB#87477): avisa uma vez e limpa os parâmetros,
  // para um refresh da página não repetir o aviso.
  useEffect(() => {
    const result = readOAuthReturn(searchParams);
    if (!result) return;
    if (result.status === "connected") {
      toast({ title: t("integrations.oauth.connected") });
    } else if (result.status === "connected_refresh_failed") {
      toast({
        title: t("integrations.oauth.connectedRefreshFailed"),
        description: result.message ?? undefined,
        variant: "destructive",
      });
    } else {
      toast({
        title: t("integrations.oauth.failed"),
        description: result.message ?? undefined,
        variant: "destructive",
      });
    }
    setSearchParams(
      (params) => {
        params.delete("oauth");
        params.delete("message");
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, toast, t]);

  const goTo = (next: IntegrationTab) => {
    setSearchParams(
      (params) => {
        params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  };

  const back = () => navigate("/integrations");

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !integration) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t("integrations.detail.back")}
        </Button>
        <p className="text-sm text-destructive">{t("integrations.detail.notFound")}</p>
      </div>
    );
  }

  const kind = instanceKind(integration);
  const logoUrl = sameOriginProxyUrl(integration.logo_url || template?.logo_url || null);
  const oauthStatus = kind === "mcp" ? mcpOAuthOf(integration)?.status : undefined;
  const description = getIntegrationDescription(integration, i18n.language);

  return (
    <div className="w-full animate-fade-in">
      <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={back}>
        <ArrowLeft className="h-4 w-4" />
        {t("integrations.detail.back")}
      </Button>

      {/* Identidade: fora das abas, porque vale para todas elas. */}
      <div className="flex items-start gap-5 pb-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-4 ring-background">
          {logoUrl ? (
            <img src={logoUrl} alt={integration.label} className="h-full w-full object-contain p-2" />
          ) : (
            <Server className="h-6 w-6 text-primary" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold">{integration.title || integration.label}</h2>
            <Badge variant="secondary">{t(`integrations.kind.${kind}`)}</Badge>
            {oauthStatus && oauthStatus !== "connected" ? (
              <Badge variant="warning">{t(`integrations.oauth.status.${oauthStatus}`)}</Badge>
            ) : (
              <Badge variant={integration.status === "active" ? "success" : "outline"}>
                {t(`integrations.status.${integration.status}`, t("integrations.status.unknown"))}
              </Badge>
            )}
          </div>
          {integration.title && integration.title !== integration.label && (
            <p className="text-xs text-muted-foreground/80">{integration.label}</p>
          )}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>

      <Tabs value={tab} className="w-full" onValueChange={(value) => goTo(value as IntegrationTab)}>
        <TabsList>
          <TabsTrigger value="general" className="gap-1">
            <Info className="h-4 w-4" />
            {t("integrations.detail.tabs.general")}
          </TabsTrigger>
          <TabsTrigger value="connection" className="gap-1">
            <Plug className="h-4 w-4" />
            {t("integrations.detail.tabs.connection")}
          </TabsTrigger>
          <TabsTrigger value="tools" className="gap-1">
            <Wrench className="h-4 w-4" />
            {t("integrations.detail.tabs.tools")}
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-1">
            <Activity className="h-4 w-4" />
            {t("integrations.detail.tabs.usage")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <IntegrationGeneralTab integration={integration} />
        </TabsContent>
        <TabsContent value="connection" className="mt-6">
          <IntegrationConnectionTab integration={integration} template={template ?? null} />
        </TabsContent>
        <TabsContent value="tools" className="mt-6">
          <IntegrationToolsTab integration={integration} />
        </TabsContent>
        <TabsContent value="usage" className="mt-6">
          {/* Só busca o uso quando a aba está aberta. */}
          {tab === "usage" && <IntegrationUsageTab integration={integration} />}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationDetailPage;
